/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */

import { ROUTES } from "@nf/constants";
import { ReactElement } from "react";
import type { GetServerSideProps, GetServerSidePropsContext } from "next";

import DetailsLayout from "layouts/DetailsLayout";
import CollectionScreen, {
  filterOptions,
  type CollectionScreenProps,
} from "screens/CollectionScreen";
import { buildSortField } from "helpers";

export const getServerSideProps: GetServerSideProps = async (
  ctx: GetServerSidePropsContext
) => {
  const { getCommonServerSideProps } = await import(
    "helpers/getCommonServerSideProps"
  );

  const setCacheHeader = await import("helpers/cacheHeaderHelper").then(
    (mod) => mod.setCacheHeader
  );

  setCacheHeader(ctx.res);

  const { commonProps, DataProvider } = await getCommonServerSideProps(ctx);
  const mediaId = ctx.params?.id?.toString() || -1;

  const queryParams = ctx.query || undefined;
  const sortParam = Array.isArray(queryParams?.sort)
    ? queryParams.sort[0]
    : queryParams.sort;

  DataProvider.setCountry(commonProps.country);

  const media = await DataProvider.getMedia({
    MediaId: mediaId,
    QueryParams: {
      sort: buildSortField(sortParam, commonProps.lang),
    },
    ...filterOptions,
  });

  if (commonProps?.isChild && media?.ContentAdvisory) {
    return {
      redirect: {
        destination: `/${commonProps.country}/${commonProps.lang}${ROUTES.KIDS}`,
      },
      props: {
        ...commonProps,
      },
    };
  }

  return {
    props: {
      ...commonProps,
      media,
    },
  };
};

const CollectionScreenWrapper = (props: CollectionScreenProps) => {
  return <CollectionScreen {...props} />;
};

CollectionScreenWrapper.getLayout = function getLayout(page: ReactElement) {
  const { props } = page;
  return <DetailsLayout config={props.config}>{page}</DetailsLayout>;
};

export default CollectionScreenWrapper;
